/**
 * Regras de senha forte (#289)
 *
 * Mesmas regras e mensagens do servidor (packages/backend/src/shared/utils/passwordPolicy.ts).
 * Símbolo: qualquer caractere que não seja letra, número ou espaço (ex.: @ # ! . - _).
 *
 * @author Equipe VendeAi
 * @since 1.9.0
 */

import { z } from 'zod';

export const PASSWORD_MIN_LENGTH = 8;
export const PASSWORD_MAX_LENGTH = 128;

export interface PasswordRule {
  /** Texto curto da lista na tela */
  label: string;
  /** Mensagem quando a regra não é atendida */
  message: string;
  test: (password: string) => boolean;
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    label: `Pelo menos ${PASSWORD_MIN_LENGTH} caracteres`,
    message: `A senha precisa ter pelo menos ${PASSWORD_MIN_LENGTH} caracteres`,
    test: p => p.length >= PASSWORD_MIN_LENGTH,
  },
  {
    label: 'Uma letra minúscula',
    message: 'A senha precisa ter pelo menos uma letra minúscula',
    test: p => /\p{Ll}/u.test(p),
  },
  {
    label: 'Uma letra maiúscula',
    message: 'A senha precisa ter pelo menos uma letra maiúscula',
    test: p => /\p{Lu}/u.test(p),
  },
  {
    label: 'Um número',
    message: 'A senha precisa ter pelo menos um número',
    test: p => /\p{N}/u.test(p),
  },
  {
    label: 'Um símbolo (ex.: @ # ! . -)',
    message: 'A senha precisa ter pelo menos um símbolo (ex.: @ # ! . -)',
    test: p => /[^\p{L}\p{N}\s]/u.test(p),
  },
];

/** Texto de ajuda com todas as regras */
export const PASSWORD_HINT =
  'Mínimo de 8 caracteres, com letra minúscula, letra maiúscula, número e símbolo (ex.: @ # ! . -).';

/** Primeira regra não atendida (null = senha forte) */
export function passwordProblem(password: string): string | null {
  if (password.length > PASSWORD_MAX_LENGTH) {
    return `A senha pode ter no máximo ${PASSWORD_MAX_LENGTH} caracteres`;
  }
  return PASSWORD_RULES.find(rule => !rule.test(password))?.message ?? null;
}

/** Campo zod de senha forte (mesmas mensagens do servidor) */
export function strongPasswordField(requiredMessage = 'Crie uma senha') {
  return z
    .string()
    .min(1, requiredMessage)
    .superRefine((value, ctx) => {
      if (!value) return;
      const problem = passwordProblem(value);
      if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
    });
}
